import { useState } from 'react';
import { MainLayout } from '@templates/index';
import { LEGAL_CONFIG } from '@/config/legal';
import { Link } from 'react-router-dom';
import { supportService } from '@services/supportService';
import { useAuthStore } from '@store/authStore';

interface ContactForm {
  subject: string;
  description: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
}

const SUBJECT_OPTIONS = [
  { value: 'Sipariş Sorunu', label: 'Sipariş Sorunu' },
  { value: 'Ödeme Sorunu', label: 'Ödeme Sorunu' },
  { value: 'Teslimat Sorunu', label: 'Teslimat Sorunu' },
  { value: 'Hesap Sorunu', label: 'Hesap Sorunu' },
  { value: 'Restoran Başvurusu', label: 'Restoran Başvurusu' },
  { value: 'Kurye Başvurusu', label: 'Kurye Başvurusu' },
  { value: 'Diğer', label: 'Diğer' },
];

const ContactPage = () => {
  const { isAuthenticated } = useAuthStore();
  const [form, setForm] = useState<ContactForm>({
    subject: '',
    description: '',
    priority: 'MEDIUM',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof ContactForm, string>>>({});
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const validate = (): boolean => {
    const newErrors: typeof errors = {};
    if (!form.subject) newErrors.subject = 'Lütfen bir konu seçin';
    if (!form.description.trim() || form.description.trim().length < 10) {
      newErrors.description = 'Mesajınız en az 10 karakter olmalıdır';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus('submitting');
    try {
      await supportService.createTicket({
        subject: form.subject,
        description: form.description.trim(),
        priority: form.priority,
      });
      setStatus('success');
      setForm({ subject: '', description: '', priority: 'MEDIUM' });
    } catch {
      setStatus('error');
    }
  };

  return (
    <MainLayout>
      <div className="max-w-5xl mx-auto py-12 px-4">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">İletişim</h1>
          <p className="text-gray-500 max-w-lg mx-auto">
            Sorularınız, önerileriniz veya iş birliği teklifleriniz için bize ulaşın.
            En kısa sürede geri döneceğiz.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
          {/* Contact Info */}
          <aside className="md:col-span-2 space-y-4">
            <div className="bg-white border border-gray-200 rounded-2xl p-6 space-y-5">
              <ContactInfoItem
                icon="📧"
                title="E-posta"
                value={LEGAL_CONFIG.email}
                href={`mailto:${LEGAL_CONFIG.email}`}
              />
              <ContactInfoItem
                icon="📞"
                title="Telefon"
                value={LEGAL_CONFIG.phone}
                href={`tel:${LEGAL_CONFIG.phone.replace(/\s/g, '')}`}
              />
              <ContactInfoItem
                icon="📍"
                title="Adres"
                value={LEGAL_CONFIG.fullAddress}
              />
              <ContactInfoItem
                icon="🌐"
                title="Web"
                value="lezzetyolda.com"
                href={LEGAL_CONFIG.website}
                external
              />
            </div>

            <div className="bg-[#E63946]/5 border border-[#E63946]/20 rounded-2xl p-5">
              <p className="text-sm font-semibold text-gray-800 mb-1">Restoran veya Kurye Başvurusu</p>
              <p className="text-sm text-gray-600 mb-3">
                Platformumuza katılmak için aşağıdaki formu doldurun veya doğrudan e-posta gönderin.
              </p>
              <a
                href={`mailto:${LEGAL_CONFIG.email}?subject=Restoran%20Ba%C5%9Fvurusu`}
                className="inline-block text-sm font-semibold text-[#E63946] hover:underline"
              >
                {LEGAL_CONFIG.email} →
              </a>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-5">
              <p className="text-sm font-semibold text-gray-800 mb-3">Hızlı Bağlantılar</p>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/faq" className="text-gray-600 hover:text-[#E63946] transition-colors">
                    → Sıkça Sorulan Sorular
                  </Link>
                </li>
                <li>
                  <Link to="/support" className="text-gray-600 hover:text-[#E63946] transition-colors">
                    → Destek Taleplerim
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-gray-600 hover:text-[#E63946] transition-colors">
                    → Kullanım Koşulları
                  </Link>
                </li>
              </ul>
            </div>
          </aside>

          {/* Form */}
          <div className="md:col-span-3">
            <div className="bg-white border border-gray-200 rounded-2xl p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-5">Mesaj Gönderin</h2>

              {!isAuthenticated && (
                <div className="mb-5 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <p className="text-sm text-amber-800">
                    Mesajınızı takip edebilmek için{' '}
                    <Link to="/login" className="font-semibold underline">
                      giriş yapmanız
                    </Link>{' '}
                    önerilir. Giriş yapmadan da e-posta ile ulaşabilirsiniz.
                  </p>
                </div>
              )}

              {status === 'success' ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl">✅</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Mesajınız Alındı</h3>
                  <p className="text-gray-500 text-sm mb-6">
                    Destek talebiniz oluşturuldu. En kısa sürede size geri döneceğiz.
                  </p>
                  <div className="flex flex-wrap justify-center gap-3">
                    <Link
                      to="/support"
                      className="px-5 py-2.5 bg-[#E63946] text-white rounded-xl text-sm font-semibold hover:bg-red-600 transition-colors"
                    >
                      Taleplerime Git
                    </Link>
                    <button
                      onClick={() => setStatus('idle')}
                      className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors"
                    >
                      Yeni Mesaj
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Konu <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={form.subject}
                      onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                      className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E63946] focus:border-transparent bg-white ${
                        errors.subject ? 'border-red-400' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Konu seçin...</option>
                      {SUBJECT_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    {errors.subject && (
                      <p className="text-xs text-red-500 mt-1">{errors.subject}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Öncelik
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      {(
                        [
                          { value: 'LOW', label: 'Düşük' },
                          { value: 'MEDIUM', label: 'Orta' },
                          { value: 'HIGH', label: 'Yüksek' },
                          { value: 'URGENT', label: 'Acil' },
                        ] as const
                      ).map((p) => (
                        <button
                          key={p.value}
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, priority: p.value }))}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                            form.priority === p.value
                              ? 'bg-[#E63946] text-white border-[#E63946]'
                              : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Mesajınız <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={form.description}
                      onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                      rows={5}
                      maxLength={4000}
                      placeholder="Sorununuzu veya talebinizi detaylı olarak açıklayın..."
                      className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E63946] focus:border-transparent resize-none ${
                        errors.description ? 'border-red-400' : 'border-gray-300'
                      }`}
                    />
                    <div className="flex justify-between mt-1">
                      {errors.description ? (
                        <p className="text-xs text-red-500">{errors.description}</p>
                      ) : (
                        <span />
                      )}
                      <span className="text-xs text-gray-400">
                        {form.description.length}/4000
                      </span>
                    </div>
                  </div>

                  {status === 'error' && (
                    <p className="text-sm text-red-500">
                      Mesaj gönderilemedi. Lütfen tekrar deneyin veya{' '}
                      <a href={`mailto:${LEGAL_CONFIG.email}`} className="underline">
                        e-posta gönderin
                      </a>
                      .
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'submitting' || !isAuthenticated}
                    className="w-full py-3 bg-[#E63946] text-white rounded-xl text-sm font-semibold hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {status === 'submitting'
                      ? 'Gönderiliyor...'
                      : !isAuthenticated
                        ? 'Göndermek için giriş yapın'
                        : 'Mesajı Gönder'}
                  </button>

                  {!isAuthenticated && (
                    <p className="text-center text-xs text-gray-500">
                      veya{' '}
                      <a href={`mailto:${LEGAL_CONFIG.email}`} className="text-[#E63946] hover:underline">
                        {LEGAL_CONFIG.email}
                      </a>{' '}
                      adresine doğrudan e-posta gönderin
                    </p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

function ContactInfoItem({
  icon,
  title,
  value,
  href,
  external,
}: {
  icon: string;
  title: string;
  value: string;
  href?: string;
  external?: boolean;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 bg-[#E63946]/8 rounded-xl flex items-center justify-center shrink-0 text-base">
        {icon}
      </div>
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
          {title}
        </p>
        {href ? (
          <a
            href={href}
            target={external ? '_blank' : undefined}
            rel={external ? 'noopener noreferrer' : undefined}
            className="text-sm text-gray-800 hover:text-[#E63946] transition-colors"
          >
            {value}
          </a>
        ) : (
          <p className="text-sm text-gray-800 leading-relaxed">{value}</p>
        )}
      </div>
    </div>
  );
}

export default ContactPage;
